import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchAgeGroups, fetchCategories } from "@/api";
import type { ApiAgeGroup, ApiCategory } from "@/api/types";
import { getAgeGroupUi, getAgeRangeLabel, getCategoryUi } from "@/constants/ui";
import IconRenderer from "@/components/feature/IconRenderer";
import { useDarkMode } from "@/site/DarkModeContext";

export default function Home() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const { isDarkMode } = useDarkMode();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [ageGroups, setAgeGroups] = useState<ApiAgeGroup[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleSelectAge = (ageGroupId: number) => {
    navigate(`/assessment/${ageGroupId}`);
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    Promise.all([fetchAgeGroups(i18n.language), fetchCategories(i18n.language)])
      .then(([ageData, categoryData]) => {
        if (!isMounted) return;
        setAgeGroups(ageData);
        setCategories(categoryData);
      })
      .catch(() => {
        if (!isMounted) return;
        setAgeGroups([]);
        setCategories([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-[#FFFBF7] dark:bg-zinc-950 text-gray-800 dark:text-zinc-100 transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative w-full pt-20 md:pt-24 pb-12 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20soft%20pastel%20abstract%20background%20with%20gentle%20flowing%20shapes%20in%20cream%20peach%20and%20light%20coral%20colors%2C%20subtle%20organic%20curves%2C%20minimal%20clean%20design%20suitable%20for%20children%20education%20website%20hero%20background%2C%20soft%20lighting%2C%20no%20text&width=1920&height=800&seq=201&orientation=landscape"
            alt=""
            className="w-full h-full object-cover dark:opacity-10 dark:mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 dark:from-zinc-950/40 via-transparent to-[#FFFBF7] dark:to-zinc-950" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 max-w-7xl mx-auto">
            {/* Text Content */}
            <div className={`flex-1 text-center ${isRTL ? "lg:text-right" : "lg:text-left"}`}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-400/10 text-primary-500 text-sm font-medium mb-6">
                <i className="ri-shield-check-line" />
                <span>{t("trustedBy")}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy-400 dark:text-zinc-100 leading-tight mb-4">
                {t("heroTitle")}
              </h1>
              <p className="text-base md:text-lg text-gray-600 dark:text-zinc-400 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                {t("heroSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <button
                  onClick={() => {
                    const el = document.getElementById("age-groups");
                    el?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="px-8 py-4 bg-primary-400 hover:bg-primary-500 text-white rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {t("startAssessment")}
                  {isRTL ? <i className="ri-arrow-left-line" /> : <i className="ri-arrow-right-line" />}
                </button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="flex-1 flex justify-center">
              <div className="relative w-full max-w-md lg:max-w-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20diverse%20children%20playing%20with%20colorful%20educational%20toys%20and%20building%20blocks%20in%20a%20bright%20warm%20room%2C%20soft%20natural%20lighting%2C%20warm%20pastel%20tones%2C%20children%20development%20and%20learning%20concept%2C%20professional%20photography%20style%2C%20cream%20and%20peach%20color%20palette&width=600&height=500&seq=202&orientation=landscape"
                  alt="Children playing"
                  className="w-full h-auto rounded-3xl object-cover"
                />
                <div className="absolute bottom-4 right-4 rtl:right-auto rtl:left-4 md:-bottom-6 md:-right-6 md:rtl:-right-auto md:rtl:-left-6 bg-white dark:bg-zinc-900 rounded-2xl p-3 md:p-4 shadow-lg border dark:border-zinc-800">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-teal-400/20 flex items-center justify-center">
                      <i className="ri-emotion-happy-line text-teal-500 text-sm md:text-base" />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm font-semibold text-navy-400 dark:text-zinc-100">{t("smartAssessment")}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-400">{t("threePillarsShort")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-400 dark:text-zinc-100 mb-3">
              {t("howItWorks")}
            </h2>
            <p className="text-gray-500 dark:text-zinc-400 text-base md:text-lg">
              {t("methodologySubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: "ri-calendar-check-line", color: "bg-primary-400/10 text-primary-400", title: t("step1Title"), desc: t("step1Desc") },
              { icon: "ri-checkbox-multiple-line", color: "bg-teal-400/10 text-teal-400", title: t("step2Title"), desc: t("step2Desc") },
              { icon: "ri-bar-chart-box-line", color: "bg-gold-400/10 text-gold-500", title: t("step3Title"), desc: t("step3Desc") },
            ].map((step, index) => (
              <div
                key={index}
                className="bg-white dark:bg-zinc-900 rounded-2xl p-6 md:p-8 border border-gray-100 dark:border-zinc-800 hover:border-gray-200 dark:hover:border-zinc-700 transition-all duration-300 hover:shadow-md group"
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl ${step.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <i className={`${step.icon} text-2xl md:text-3xl`} />
                </div>
                <div className="w-8 h-8 rounded-full bg-navy-400 dark:bg-zinc-800 text-white dark:text-zinc-200 flex items-center justify-center text-sm font-bold mb-4 border dark:border-zinc-700">
                  {index + 1}
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-navy-400 dark:text-zinc-100 mb-2">{step.title}</h3>
                <p className="text-sm md:text-base text-gray-500 dark:text-zinc-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-12 md:py-16 bg-white dark:bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-400 dark:text-zinc-100 mb-3">
              {t("threePillarsTitle")}
            </h2>
            <p className="text-gray-500 dark:text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
              {t("threePillarsDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {categories.length === 0 ? (
              <div className="text-center text-gray-400 col-span-full">
                {isLoading ? t("loading") : t("noData")}
              </div>
            ) : (
              categories.map((cat) => {
                const ui = getCategoryUi(cat.key);
                const borderStyle = isDarkMode
                  ? { borderColor: `${cat.colorHex}40` }
                  : { borderColor: `${cat.colorHex}4D`, backgroundColor: `${cat.colorHex}1A` };

                return (
                  <div
                    key={cat.id}
                    className="rounded-2xl p-6 md:p-8 border hover:shadow-md transition-all duration-300 bg-white dark:bg-zinc-900/40"
                    style={borderStyle}
                  >
                    <div
                      className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-5"
                      style={{ backgroundColor: `${cat.colorHex}20` }}
                    >
                      <IconRenderer
                        iconUrl={cat.iconUrl}
                        fallbackIcon={ui.icon}
                        color={cat.colorHex}
                        size={28}
                        className="text-2xl md:text-3xl"
                      />
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-navy-400 dark:text-zinc-100 mb-3">{cat.name}</h3>
                    <p className="text-sm md:text-base text-gray-500 dark:text-zinc-400 leading-relaxed">
                      {cat.key === "physical" && t("physicalDesc")}
                      {cat.key === "cognitive" && t("cognitiveDesc")}
                      {cat.key === "social" && t("socialDesc")}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Age Group Selection */}
      <section id="age-groups" className="w-full px-4 md:px-8 lg:px-16 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-400 dark:text-zinc-100 mb-3">
              {t("selectAge")}
            </h2>
            <p className="text-gray-500 dark:text-zinc-400 text-base md:text-lg">
              {t("selectAgeSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
            {ageGroups.length === 0 ? (
              <div className="text-center text-gray-400 col-span-full">
                {isLoading ? t("loading") : t("noData")}
              </div>
            ) : (
              ageGroups.map((group) => {
                const ui = getAgeGroupUi(group.key);
                const displayAge = getAgeRangeLabel(group.minMonths, group.maxMonths);

                return (
                  <button
                    key={group.id}
                    onClick={() => handleSelectAge(group.id)}
                    onMouseEnter={() => setHoveredCard(String(group.id))}
                    onMouseLeave={() => setHoveredCard(null)}
                    className="group relative bg-white dark:bg-zinc-900 rounded-2xl p-4 md:p-6 border-2 text-start transition-all duration-300 hover:shadow-lg hover:-translate-y-1 dark:border-zinc-800"
                    style={{
                      borderColor: hoveredCard === String(group.id) ? ui.color : isDarkMode ? "#27272a" : "#f0f0f0",
                    }}
                  >
                    <div
                      className="w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${ui.color}15` }}
                    >
                      <i className={`${ui.icon} text-lg md:text-2xl`} style={{ color: ui.color }} />
                    </div>
                    <h3 className="text-sm md:text-lg font-bold text-navy-400 dark:text-zinc-100 mb-1">
                      {group.title}
                    </h3>
                    {ui.subtitleKey && <p className="text-xs md:text-sm text-gray-400 dark:text-zinc-550 mb-3">{t(ui.subtitleKey)}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] md:text-xs font-medium px-2 py-1 md:px-3 rounded-full" style={{ backgroundColor: `${ui.color}15`, color: ui.color }}>
                        {displayAge} {group.maxMonths <= 12 ? t("months") : t("years")}
                      </span>
                      {isRTL ? (
                        <i className="hidden sm:block ri-arrow-left-line text-gray-300 dark:text-zinc-700 group-hover:text-primary-400 transition-colors" />
                      ) : (
                        <i className="hidden sm:block ri-arrow-right-line text-gray-300 dark:text-zinc-700 group-hover:text-primary-400 transition-colors" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Happy%20children%20playing%20together%20in%20a%20bright%20colorful%20playroom%20with%20educational%20toys%20scattered%20around%2C%20warm%20natural%20lighting%2C%20diverse%20group%20of%20kids%20laughing%20and%20learning%2C%20soft%20pastel%20color%20palette%20with%20cream%20peach%20and%20mint%20tones%2C%20professional%20photography&width=1200&height=400&seq=203&orientation=landscape"
              alt="Children playing"
              className="w-full h-56 md:h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-400/80 dark:from-zinc-950/90 via-navy-400/40 dark:via-zinc-900/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-center">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-4">
                {t("ctaTitle")}
              </h3>
              <button
                onClick={() => {
                  const el = document.getElementById("age-groups");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-8 py-3 bg-white dark:bg-zinc-900 text-navy-400 dark:text-zinc-100 rounded-full font-semibold text-base hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
              >
                {t("startAssessment")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
